import { useCallback, useContext, useEffect } from 'react';
import { identity } from 'lodash';

import {
	CustomizerElement,
} from '../components/interfaces';
import { AvailableTools, AvailableToolsContext } from '../state/availableTools';
import { useAppStore } from '../state/store';

export const useAvailableToolsForItem = () => {
	const useStore = useContext(AvailableToolsContext);
	if (!useStore) {
		throw new Error('useAvailableToolsForItem must be used within a AvailableToolsProvider');
	}
	const global = useStore().global;
	const byGuids = useStore((x) => x.byGuids);
	const getByGuid = useStore(x => x.getByGuids);
	return useCallback((item?: CustomizerElement | string | null) => {
		if (!item) {
			return global;
		}
		if (typeof item === 'string') {
			return byGuids.get(item) || getByGuid(item);
		}
		return byGuids.get(item.guid) || getByGuid(item.guid);
	}, [byGuids, getByGuid, global]);
};

const useListenToAvailableTools = (guid?: string | null, enabled: boolean = true) => {
	const useStore = useContext(AvailableToolsContext);
	if (!useStore) {
		throw new Error('useAvailableToolsForItem must be used within a AvailableToolsProvider');
	}

	useEffect(() => {
		if (!enabled) {
			return undefined;
		}
		if (!guid) {
			return undefined;
		}
		useStore.setState((prev) => {
			prev.activeListenersCount.set(guid, (prev.activeListenersCount.get(guid) || 0) + 1);
			return prev;
		})
		return () => {
			useStore.setState((prev) => {
				prev.activeListenersCount.set(guid, (prev.activeListenersCount.get(guid) || 0) - 1);
				return prev;
			})
		}
	}, [guid, useStore, enabled]);
}

export const useAvailableToolsForItemValue = <Slice = AvailableTools,>(
	guid: string,
	selector: (tools: AvailableTools) => Slice = identity,
	enabled: boolean = true,
) => {
	const useStore = useContext(AvailableToolsContext);
	if (!useStore) {
		throw new Error('useAvailableToolsForItem must be used within a AvailableToolsProvider');
	}
	useListenToAvailableTools(guid, enabled);
	return useStore((x) => selector(x.getByGuids(guid)));
};

export const useAvailableToolsGlobal =  <Slice = AvailableTools,>(selector: (tools: AvailableTools) => Slice = identity,) => {
	const useStore = useContext(AvailableToolsContext);
	if (!useStore) {
		throw new Error('useAvailableToolsForItem must be used within a AvailableToolsProvider');
	}
	return useStore((x) => selector(x.global));
}

/**
 * @deprecated use useAvailableToolsForItemValue or useAvailableToolsGlobal instead
 */
export const useAvailableToolsForSelectedItem = <Slice = AvailableTools,>(
	selector: (tools: AvailableTools) => Slice = identity,
) => {
	const useStore = useContext(AvailableToolsContext);
	if (!useStore) {
		throw new Error('useAvailableToolsForItem must be used within a AvailableToolsProvider');
	}

	const guid = useAppStore((x) => x.selectedItemGuid);

	useListenToAvailableTools(guid);

	return useStore((x) => {
		if (!guid) {
			return selector(x.global);
		}
		return selector(x.getByGuids(guid));
	});
};
